import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import '../styles/cookie-consent.css';

/**
 * Cookie consent banner (issue #264).
 *
 * WHY THIS IS HAND-ROLLED RATHER THAN CookieConsent / Osano / OneTrust, which
 * the issue suggested evaluating: all three are hosted SDKs that inject a
 * remote script at the top of every page. That means (a) a third-party
 * request on every page load of a site whose whole pitch is trustworthy
 * assessment, (b) the consent tool itself dropping identifiers before the
 * visitor has consented to anything, (c) another origin to allow through CSP,
 * and (d) a per-domain licence cost for the tiers that actually do
 * geo-targeting. What we need is three buttons and a stored preference —
 * about 120 lines. If a full IAB TCF / vendor-list implementation is ever
 * required (ad tech, not our case), swap this for a licensed CMP then.
 *
 * Storage is localStorage, wrapped: Safari private mode and "block all
 * cookies" both make it throw on access, and an exception here would take the
 * whole app down through the error boundary. If it is unreadable the banner
 * simply shows again next visit, which is the safe direction to fail —
 * re-asking is annoying, assuming consent is not.
 *
 * CONSENT_VERSION is part of the stored value on purpose: bump it when the
 * cookie policy materially changes and every visitor is re-asked, which is
 * what both GDPR and CCPA expect after a material change.
 */
const STORAGE_KEY = 'tg_cookie_consent';
const CONSENT_VERSION = 1;

const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

const CATEGORIES = [
  {
    key: 'necessary',
    title: 'Strictly necessary',
    locked: true,
    body: 'Sign-in, security and remembering your theme. The site cannot work without these, so they are always on and are not used to track you.',
  },
  {
    key: 'analytics',
    title: 'Analytics',
    body: 'Aggregate usage — which features get used and where people get stuck. Helps us decide what to build next.',
  },
  {
    key: 'marketing',
    title: 'Marketing',
    body: 'Measuring whether a campaign brought you here. Off unless you turn it on.',
  },
];

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // A stored decision against an older policy version is treated as absent.
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return { ...DEFAULT_PREFS, ...parsed.prefs, necessary: true };
  } catch (err) {
    return null;
  }
}

function writeStored(prefs) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CONSENT_VERSION, decidedAt: new Date().toISOString(), prefs })
    );
  } catch (err) {
    /* private mode / cookies blocked — banner reappears next visit, which is
       the correct way to fail. Nothing to recover here. */
  }
}

/** Lets anything else on the page read consent without importing this file. */
function publish(prefs) {
  try {
    window.__tgCookieConsent = prefs;
    window.dispatchEvent(new CustomEvent('tg:cookie-consent', { detail: prefs }));
  } catch (err) { /* non-fatal */ }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const panelRef = useRef(null);
  const firstBtnRef = useRef(null);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      publish(stored);
      return;                       // already decided — never show again
    }
    // Let the page paint first; the banner is not the first thing anyone needs.
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, []);

  const decide = useCallback((next) => {
    const final = { ...next, necessary: true };
    writeStored(final);
    publish(final);
    setVisible(false);
    setCustomising(false);
  }, []);

  const acceptAll = useCallback(
    () => decide({ necessary: true, analytics: true, marketing: true }),
    [decide]
  );
  const rejectAll = useCallback(
    () => decide({ necessary: true, analytics: false, marketing: false }),
    [decide]
  );
  const saveChoices = useCallback(() => decide(prefs), [decide, prefs]);

  // Esc closes the customise panel, returning to the bar rather than
  // dismissing — closing without a decision must not count as consent.
  useEffect(() => {
    if (!customising) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCustomising(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [customising]);

  useEffect(() => {
    if (visible && firstBtnRef.current) firstBtnRef.current.focus();
  }, [visible, customising]);

  if (!visible) return null;

  return (
    <div
      className={`cc-root${customising ? ' is-open' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-label="Cookie preferences"
      ref={panelRef}
    >
      <div className="cc-bar">
        <div className="cc-icon" aria-hidden="true"><Cookie size={18} /></div>

        <div className="cc-copy">
          <p className="cc-title">We use a few cookies.</p>
          <p className="cc-text">
            The necessary ones keep you signed in. The rest are optional and off until you say
            otherwise. See our <Link to="/policies/cookie-policy">Cookie Policy</Link> and{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </div>

        <div className="cc-actions">
          <button
            type="button"
            className="cc-btn cc-btn-ghost"
            onClick={() => setCustomising(v => !v)}
            aria-expanded={customising}
            aria-controls="cc-panel"
          >
            Customise
          </button>
          <button type="button" className="cc-btn cc-btn-quiet" onClick={rejectAll}>
            Reject
          </button>
          <button
            type="button"
            className="cc-btn cc-btn-primary"
            onClick={acceptAll}
            ref={firstBtnRef}
          >
            Accept
          </button>
        </div>
      </div>

      {customising && (
        <div className="cc-panel" id="cc-panel">
          <button
            type="button"
            className="cc-panel-close"
            onClick={() => setCustomising(false)}
            aria-label="Close cookie preferences"
          >
            <X size={16} aria-hidden="true" />
          </button>

          {CATEGORIES.map(cat => (
            <div className="cc-cat" key={cat.key}>
              <label className="cc-cat-head" htmlFor={`cc-${cat.key}`}>
                <input
                  id={`cc-${cat.key}`}
                  type="checkbox"
                  checked={cat.locked ? true : prefs[cat.key]}
                  disabled={cat.locked}
                  onChange={e =>
                    setPrefs(p => ({ ...p, [cat.key]: e.target.checked }))
                  }
                />
                <span className="cc-cat-title">{cat.title}</span>
                {cat.locked && <span className="cc-always">Always on</span>}
              </label>
              <p className="cc-cat-body">{cat.body}</p>
            </div>
          ))}

          <div className="cc-panel-actions">
            <button type="button" className="cc-btn cc-btn-quiet" onClick={rejectAll}>
              Reject all
            </button>
            <button type="button" className="cc-btn cc-btn-primary" onClick={saveChoices}>
              Save choices
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
